import {RealtimePostgresChangesPayload, SupabaseClient} from '@supabase/supabase-js'
import {
  DefaultError,
  QueryFunction,
  QueryKey,
  Updater,
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import {useEffect, useMemo, useRef} from 'react'
import {isArray, keys, pipe, take} from 'remeda'
import {mapEntries, entries, concat, compactObj, notNullOrEmpty, deepEqual} from 'shared/collection'
import {doSafe} from 'shared/promise'
import {Json, parseOrErr, parseOrErrPartition} from 'shared/type'
import {useMemoValue} from 'shared/ui/useMemo'
import {match, P} from 'ts-pattern'
import {Primitive, ZodObject, ZodRawShape, ZodString, z} from 'zod'
import {sb} from './supabaseClient'
import {Database} from './tables.generated'
import {useLatest} from 'components/hooks'
import {listMods} from 'components/hooks/useUIState'
import {showError} from 'components/notify'
import {usePortal} from 'components/portal/portal'
import {DateTime} from 'luxon'
import {env} from 'utils/env'
import type {Profile} from 'components/portal/profileTypes'
import {sbAll} from './query'

const {debug: consoleDebug} = console

const createThrottledFn = <T extends (...args: any[]) => void>(callback: T, delay: number): T => {
  let timer: NodeJS.Timeout | null = null
  let lastCallTime: number | null = null

  return ((...args: Parameters<T>) => {
    const now = Date.now()

    const shouldCallImmediately = lastCallTime === null || now - lastCallTime > delay

    if (shouldCallImmediately) {
      callback(...args)
      lastCallTime = now
    } else {
      if (timer) {
        clearTimeout(timer)
      }

      timer = setTimeout(() => {
        callback(...args)
        lastCallTime = Date.now()
      }, delay)
    }
  }) as T
}

const debouncedInvalidators = new Map<
  string,
  {
    invalidate: (key: QueryKey) => void
    refCount: number
  }
>()

type Tables = Database['public']['Tables']
type Views = Database['public']['Views']
type TableName = keyof Tables
type TableOrViewName = keyof Tables | keyof Views
type Filter = Record<string, Primitive | Primitive[]>

/** Lightweight wrapper for useQuery that adds `setQueryData` function */
export const useQueryEx = <
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
): UseQueryResult<TData, TError> & {setQueryData: (data: TData) => void} => {
  const queryClient = useQueryClient()
  const result = useQuery(options)
  const setQueryData = (data: TData) => {
    queryClient.setQueryData(options.queryKey, data as any)
  }
  return {...result, setQueryData}
}

/**
 * A function wrapping useQuery, that also performs automatic refresh based on supabase realtime events.
 * Use `useQuerySbRead` or `useQuerySbRead` instead if your following the conventions. Fall back to this if you need more control`
 *
 * ```
 *const {data} = useQuerySb(
 *   [{table: 'people', key: 'usePeople'}, {person_id}],
 *   async ({queryKey: [{table}, filter]}) => {
 *     const {data, error} = await sb.from(table).select('*').match({...filter, deleted: false})
 *     if (data == null || error) throw new Error(error.message)
 *     return data
 *   },
 *   {enabled: question_id != undefined}
 * )
 * ```
 * @param table the table to query
 * @param queryKey an tuple of dependencies for the query `[table & key, sub-filter, filter]`. a unique string for this query. Usually the name of the hook/component
 * @param queryFn an async function that returns the data
 * @param options same as react-query options
 * @returns the react query results
 */
export const useQuerySb = <
  TTableName extends TableName,
  TQueryFnData,
  TQueryKey extends [
    key: string,
    /** the filter is used to subscribe to changes in the given table.
     *  Only the first will be users for server side filtering, but all will be used as a unique channel id */
    subFilter: TStaticFilter,
    contextFilter?: TFilter
  ],
  TStaticFilter extends Filter,
  TFilter extends Filter
>(
  table: TTableName,
  queryKey: TQueryKey,
  queryFn: QueryFunction<TQueryFnData, [TTableName, ...TQueryKey]>,
  options?: Omit<
    UseQueryOptions<TQueryFnData, Error, TQueryFnData, [TTableName, ...TQueryKey]>,
    'queryKey' | 'queryFn'
  > & {
    disableRealtime?: boolean
    disableRefetch?: boolean
    refetchDebounce?: number
    onChange?: (p: RealtimePostgresChangesPayload<Tables[TTableName]['Row']>) => void
    onChangeFilterOverride?: Filter
    debugName?: string
  }
) => {
  const [key, subFilter] = queryKey
  const queryClient = useQueryClient()
  const query = useQuery({...options, queryKey: [table, ...queryKey], queryFn})
  const {disableRefetch, disableRealtime, onChangeFilterOverride, debugName, enabled, refetchDebounce, onChange} =
    options ?? {}

  const fullQueryKey = useMemo(() => [table, ...queryKey], [table, queryKey])
  const queryKeyString = useMemo(() => JSON.stringify(fullQueryKey), [fullQueryKey])
  const queryClientLatest = useLatest(queryClient)
  useEffect(() => {
    let entry = debouncedInvalidators.get(queryKeyString)
    if (!entry) {
      const invalidate = (key: QueryKey) => {
        queryClientLatest.current.invalidateQueries({queryKey: key})
      }
      entry = {
        invalidate: createThrottledFn(invalidate, refetchDebounce ?? 5000),
        refCount: 0
      }
      debouncedInvalidators.set(queryKeyString, entry)
    }

    entry.refCount++

    return () => {
      const entryOnCleanup = debouncedInvalidators.get(queryKeyString)
      if (entryOnCleanup) {
        entryOnCleanup.refCount--
        if (entryOnCleanup.refCount <= 0) {
          debouncedInvalidators.delete(queryKeyString)
        }
      }
    }
  }, [queryKeyString, debugName, queryClientLatest, refetchDebounce])

  const filterToUse = onChangeFilterOverride ?? subFilter
  const firstFilterKey = keys(filterToUse)[0]
  const firstFilterValue = firstFilterKey ? filterToUse[firstFilterKey] : undefined

  useSbChannel({
    table,
    filter: firstFilterKey ? [firstFilterKey, firstFilterValue] : undefined,
    onChange: p => {
      onChange?.(p)
      if (!disableRefetch) {
        const invalidator = debouncedInvalidators.get(queryKeyString)?.invalidate
        invalidator?.(fullQueryKey)
      }
    },
    debugName,
    disabled: disableRealtime || enabled === false || env.NEXT_PUBLIC_DISABLE_REALTIME
  })

  const setQueryData = (fn: Updater<TQueryFnData | undefined, TQueryFnData | undefined>) =>
    queryClient.setQueryData([table, ...queryKey], fn)

  return {...query, setQueryData}
}

type Builder = ReturnType<SupabaseClient['from']>
type BuilderSelect = ReturnType<Builder['select']>
type BuilderRpc = ReturnType<SupabaseClient['rpc']>

export type OnDbChangeParams = {eventType: RealtimePostgresChangesPayload<any>['eventType']; id: string}

export type UseQuerySbSyncProps<TZodShape extends ZodRawShape, TPick = unknown> = {
  /** the name used for logging and query keys */
  name?: string
  /** the table to query. Used for crud operations. Use this for the underlying table and `selectFrom` if you want to read differently to write */
  table: TableOrViewName
  filter?: Filter
  dependencies?: Filter
  /** name of the filter property ot use for the channel. By default the first key in the object */
  channelFilterProp?: keyof Filter
  schema: ZodObject<TZodShape>
  debugName?: string
  /** the select query or a supabase client query. If a builder, the conditions will be used in addition to the filter */
  select?: string | ((b: Builder) => BuilderSelect)
  /** the name of the table to select (read, not write) from if you want it to be different to `tableName` */
  selectFrom?: TableOrViewName | BuilderRpc
  /** for filtering that is more complex that a match, you can just implement on the client if you want */
  clientFilter?: (r: z.infer<ZodObject<TZodShape>>) => boolean
  /** if provided, pick a subset of each row to return (after clientFilter). Using this will mean that if the resulting data matches, it will not cause a re-render */
  pick?: (r: z.infer<ZodObject<TZodShape>>) => TPick
  options?: Omit<UseQueryOptions<z.infer<ZodObject<TZodShape>>[]>, 'queryKey' | 'queryFn'>
  disableChannel?: boolean
  onFetch?: () => void
  onFetched?: (data: z.infer<ZodObject<TZodShape>>[]) => void
  onDbChange?: (p: OnDbChangeParams) => void

  // use this to specify a natural key column to use for upserts. We don't need a local copy, but to update out local data we do need it
  naturalKeyCol?: keyof z.infer<ZodObject<TZodShape>> | string
  /** if you want to override the default */
  profile?: Pick<Profile, 'id'>
}

const defaultClientFilter = () => true

/** Wraps useQuery for a given schema & supabase table with
 * rows using the row: {id:string cfg:Json} convention. Uses realtime to update rows */
export const useQuerySbRead = <TZodShape extends ZodRawShape, TPick = unknown>(
  props: UseQuerySbSyncProps<TZodShape, TPick>
) => {
  type TBase = z.infer<ZodObject<TZodShape>>
  type T = TBase & {id: string}
  type TRes = unknown extends TPick ? T : TPick
  const {
    table,
    schema,
    debugName,
    select,
    selectFrom,
    options,
    onFetch,
    onFetched,
    onDbChange,
    dependencies,
    name,
    disableChannel,
    pick: pickFn
  } = props

  const clientFilter = props.clientFilter ?? defaultClientFilter
  const channelFilterProp = props.channelFilterProp ?? (props.filter ? keys(props.filter)[0]! : undefined)
  const {ft, mode} = match(selectFrom)
    .with(P.string, P.nullish, t => ({
      ft: () =>
        pipe(t ? sb.from(t as any) : sb.from(table as any), ft =>
          match(select)
            .with(P.string, P.nullish, q => ft.select(q ?? '*'))
            .otherwise(q => q(ft))
        ),
      mode: 'select' as const
    }))
    .otherwise(q => ({ft: () => q, mode: 'rpc' as const}))

  const filter = useMemoValue(props.filter)
  const queryKey = [name ?? 'useQuerySbTable', table, filter, dependencies] as const
  const lastPicksRef = useRef<TRes[] | null>(null)

  const selectFn = (data: TBase[]): TRes[] => {
    const filtered = data.filter(clientFilter)
    if (pickFn == null) return filtered as TRes[] // react-query ues reference equality to work things out
    const picks = filtered.map(pickFn) as TRes[] // when picking we need to check outselves and return the previous ref
    if (lastPicksRef.current && deepEqual(lastPicksRef.current, picks)) return lastPicksRef.current!
    lastPicksRef.current = picks
    return picks
  }

  const q = useQuery<TBase[], Error, TRes[]>({
    ...options,
    queryKey,
    queryFn: async () => {
      onFetch?.()
      let q = ft()
      if (notNullOrEmpty(filter)) {
        if (mode == 'rpc') {
          console.warn("rpc queries don't support filters")
        } else {
          entries(filter).forEach(([k, v]) => {
            q = match(v)
              .with([], a => q.containedBy(k, a))
              .otherwise(() => q.eq(k, v))
          })
        }
      }

      const r = await sbAll(q)
      if (!r.ok) {
        console.error('error with query', table, filter ? toFlatString(filter) : 'no filter', r.message)
        throw new Error(
          `error fetching from ${table} ${filter ? `(filter ${toFlatString(filter)})` : ''}: ${r.message}`
        )
      }
      const [resOK, resErr] = parseOrErrPartition(schema, r)
      if (resErr.length > 0) {
        console.error(
          'error parsing data from ${table}',
          take(resErr, 3).map(e => e.message)
        )
        throw new Error(
          take(resErr, 3)
            .map(e => e.message)
            .join('\n')
        )
      }
      onFetched?.(resOK)
      return resOK
    },
    placeholderData: previousData => previousData,
    select: selectFn,
    notifyOnChangeProps: ['data', 'error', 'status']
  })

  const queryClient = useQueryClient()
  const setQueryData = (fn: Updater<T[] | undefined, T[] | undefined>) => queryClient.setQueryData(queryKey, fn)

  /** client side version of the filter */
  const rowMatch = (row: T) =>
    filter == null ? true : (
      mapEntries(filter, (k, v) => {
        const rowV = row[k]
        return match(v)
          .with(P.string, s => s == rowV)
          .with(P.array(P.any), v => v.includes(rowV))
          .otherwise(v => v == rowV)
      }).every(m => m)
    )

  const upsertQueryData = (row: Partial<T> & {id: string}) =>
    setQueryData(d => {
      if (!d) return d
      const res = listMods(d, {naturalKeyCol: props.naturalKeyCol, schema: schema as any, debugName}).upsert(row)
      if (res === d) {
        if (debugName) consoleDebug('useQuerySbSync.upsertQueryData same so returning the original', debugName)
        return d
      }
      return res.filter(r => rowMatch(r))
    })

  useSbChannel({
    table,
    filter: filter ? [channelFilterProp!, filter[channelFilterProp!]] : undefined,
    onChange: p => {
      setQueryData(
        d =>
          match(d as any)
            .with(P.nullish, () => d)
            .with(P.array({id: P.string}), old => {
              const list = listMods(old)
              return match(p)
                .with({eventType: P.union('INSERT', 'UPDATE'), new: P.shape({id: P.string}).select()}, o => {
                  if (debugName) consoleDebug('useQuerySbSync.setQueryData', debugName, {p, o})
                  const r = schema.partial().safeParse(o)
                  if (!r.success) {
                    console.error('useQuerySbSync.setQueryData', {p, error: r.error})
                    return d
                  }
                  return upsertQueryData(compactObj(r.data) as T)
                })
                .with({eventType: 'DELETE', old: P.shape({id: P.string}).select()}, o => list.del(o.id))
                .otherwise(() => d) as T[]
            })
            .with(
              {id: P.string},
              old =>
                match(p)
                  .with({eventType: 'UPDATE', id: old.id}, e => ({...old, ...e.new}))
                  .otherwise(() => d) as T[]
            )
            .otherwise(() => d) as T[] | undefined
      )
      const id = match(p)
        .with({eventType: P.union('INSERT', 'UPDATE'), new: P.shape({id: P.string}).select()}, o => o.id)
        .with({eventType: 'DELETE', old: P.shape({id: P.string}).select()}, o => o.id)
        .otherwise(() => undefined)
      if (id) onDbChange?.({eventType: p.eventType, id})
    },
    debugName,
    disabled: options?.enabled === false || disableChannel || env.NEXT_PUBLIC_DISABLE_REALTIME
  })

  return {...q, setQueryData, upsertQueryData}
}

/** Wraps useQuery for a given schema & supabase table with
 * rows using the row: {id:string cfg:Json} convention. Uses realtime to update rows.
 * Provides CRUD functions */
export const useQuerySbSync = <TZodShape extends ZodRawShape & {id: ZodString}, TPick = unknown>(
  props: UseQuerySbSyncProps<TZodShape, TPick>
) => {
  type T = z.infer<ZodObject<TZodShape>> & {id: string}
  const {table, schema, naturalKeyCol, debugName} = props
  const parse = (o: any) => {
    const res = parseOrErr(schema, o)
    if (!res.ok) throw new Error(res.message)
    return res.data as T
  }
  const ft = () => sb.from(table as any)
  const res = useQuerySbRead(props)
  const {upsertQueryData, data, setQueryData} = res

  const portal = usePortal()
  const profile = props.profile ?? portal.profile

  /** inserts a row into the table */
  const insertMutation = useMutation({
    mutationFn: async (row: Partial<T>) => {
      const {data: dataRaw, error} = await sb
        .from(table as any)
        .insert(row as any)
        .select()
        .single()
      const data = dataRaw ? parse(dataRaw) : undefined
      if (data) upsertQueryData(data)
      if (error) showError(`${error.message}: ${error.details}`, `Unable to save ${table}`)
      return {data, error}
    }
  })

  /** updates  a row in the table */
  const updateMutation = useMutation({
    mutationFn: async (row: Partial<T> & {id: string}) => {
      const {id, ...updates} = row
      if (debugName) consoleDebug('update', debugName, {id, updates})
      // we don't have to query if the channels are working, but best to do it as part fo the round trip as it doesn't cost much
      const {data: dataRaw, error} = await ft().update(updates).eq('id', id).select().maybeSingle()
      const data = dataRaw ? parse(dataRaw) : undefined
      if (data) upsertQueryData(data)
      if (error) showError(`${error.message}: ${error.details}`, `Unable to save ${table}`)
      return {data, error}
    }
  })

  const updateAllMutation = useMutation({
    mutationFn: async ({ids, update}: {ids: string[]; update: Partial<T>}) => {
      const {error} = await ft().update(update as Record<string, unknown>).in('id', ids)
      if (error) showError(`${error.message}: ${error.details}`, `Unable to save ${table}`)
      else
        ids.forEach(id => {
          upsertQueryData({...update, id} as Partial<T> & {id: string})
        })
      return {error}
    }
  })

  const upsertMutation = useMutation({
    /** either specify an id or a natural key, don't have nice typing for that yet */
    mutationFn: async (row: Partial<T> & {id?: string}) => {
      const {data: dataRaw, error} = await ft()
        .upsert(row as any, naturalKeyCol ? {onConflict: naturalKeyCol as string} : {})
        .select()
        .single()
      if (error) showError(`${error.message}: ${error.details}`, `Unable to save ${table}`)
      if (props.debugName) consoleDebug('useQuerySbSync.upsertMutation', debugName, {table, row})
      const data = dataRaw ? parse(dataRaw) : undefined
      if (data && data.id) upsertQueryData(data)
      else console.warn(`not upserting locally because the upserted for didn't return an id`)
      return {data, error}
    }
  })

  const updateCfg = useMutation({
    mutationFn: async ({id, cfg_updates}: {id: string; cfg_updates: Record<string, Json>}) => {
      if (!profile) return {error: 'no profile'}
      const params = {
        table_name: table,
        cfg_updates,
        id,
        profile_id: profile.id,
        updated_at: DateTime.utc().toISO()!
      }
      const {data: cfgRaw, error} = await sb.rpc('update_cfg', params)
      if (debugName) consoleDebug('useQuerySbSync.updateCfg', debugName, {table, id, cfg_updates, data: cfgRaw})
      if (error) showError(`${error.message}: ${error.details}`, `Unable to save ${table}`)
      let row = data?.find((d: any) => d?.id == id)
      if (row) upsertQueryData(parse({...row, cfg: cfgRaw}))
      return {data: row, error}
    }
  })

  const delMutation = useMutation({
    mutationFn: async (idRaw: string | string[]) => {
      const ids = isArray(idRaw) ? idRaw : [idRaw]
      if (props.debugName) consoleDebug('deleting', debugName, {table, ids})
      const {error} = await (ids.length > 1 ? ft().delete().in('id', ids) : ft().delete().eq('id', ids[0]))
      if (error) showError(`${error.message}: ${error.details}`, `Unable to delete ${table}`)
      setQueryData(d => {
        if (!d) return undefined
        return d.filter(r => !ids.includes(r.id))
      })
      return {error}
    }
  })

  const isPending =
    insertMutation.isPending || updateMutation.isPending || upsertMutation.isPending || updateAllMutation.isPending
  return {
    ...res,
    insert: insertMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    updateAll: updateAllMutation.mutateAsync,
    upsert: upsertMutation.mutateAsync,
    updateCfg: updateCfg.mutateAsync,
    /** actually deletes the row, for most tables you want to update delete column instead */
    del: delMutation.mutateAsync,
    isDirty: isPending,
    isPending
  }
}

const getFilterString = (k: string, v: Primitive | Primitive[]) =>
  `${k}=${match(v)
    .with(P.array(P.any), v => `in.(${v.join(',')})`)
    .otherwise(() => `eq.${String(v)}`)}`

type ChannelEntry = {channel: any; handlers: Set<(p: any) => void>}
const activeChannels = new Map<string, ChannelEntry>()

export const useSbChannel = <TTableName extends TableOrViewName>({
  table,
  filter,
  onChange,
  debugName,
  disabled: disabledRaw
}: {
  table: TTableName
  filter?: [string, Primitive | Primitive[]] | readonly [string, Primitive | Primitive[]]
  onChange: (
    p: RealtimePostgresChangesPayload<Tables[TTableName extends TableName ? TTableName : never]['Row']>
  ) => void
  debugName?: string
  disabled?: boolean
}) => {
  const disabled = disabledRaw ?? env.NEXT_PUBLIC_DISABLE_REALTIME
  const filterString = filter ? getFilterString(filter[0], filter[1]) : undefined
  const channelString = concat('useSbChannel', table, filterString).join('|')

  const latest = useLatest({onChange})

  useEffect(() => {
    if (debugName)
      consoleDebug(
        'useSbChannel subscribe',
        channelString,
        disabled ? 'disabled'
        : activeChannels.get(channelString) ? 'exists'
        : 'creating'
      )
    if (disabled) return

    let entry = activeChannels.get(channelString)
    if (!entry) {
      const channel = sb.channel(channelString)
      channel
        .on('postgres_changes', {event: '*', schema: 'public', table, filter: filterString}, p => {
          if (debugName)
            consoleDebug('useQuerySbSync channel postgres_changes', debugName, channelString, {table, filter})
          const e = activeChannels.get(channelString)
          e?.handlers.forEach(h => h(p))
        })
        .subscribe((s, e) => {
          if (debugName) consoleDebug('useQuerySbSync channel subscribed', debugName, channelString, {table, filter})
          if (s == 'CHANNEL_ERROR')
            consoleDebug(`${channelString} sub status: ${s}${e == null ? '' : ': ' + e.message}`)
        })
      entry = {channel, handlers: new Set()}
      activeChannels.set(channelString, entry)
    }

    const handler = (p: any) => latest.current.onChange(p as any)
    entry.handlers.add(handler)

    return () => {
      const e = activeChannels.get(channelString)
      if (!e) return
      e.handlers.delete(handler)
      if (e.handlers.size == 0) {
        e.channel.unsubscribe()
        activeChannels.delete(channelString)
      }
    }
  }, [channelString, disabled])
}

const toFlatString = (filter: Filter) =>
  entries(filter)
    .map(([k, v]) => `${k}:${isArray(v) ? v.join(',') : String(v)}`)
    .join('|')

export const parseJoinProp = <T extends ZodRawShape, P>(zObj: ZodObject<T>, joinProp: JoinProp<P>) =>
  doSafe(firstSafe(joinProp), j => zObj.parse(j))

const firstSafe = <T>(o: T | T[]) => (Array.isArray(o) ? o[0] : o)
export type JoinProp<T> = T | T[]
